/**
 * Offline inbox feed: turns story beats into notes in the book. Written against the mission service's job
 * board, so any job list works (new story jobs included): a call taken at a ringing phone box is written
 * down afterwards, a finished job brings a word from the client and points at the next one, Halloran and
 * Sid introduce themselves once you've done a job for Mags, and the law, the cells and a full wallet each
 * get a one-off note. Every message has a key, so nothing is ever posted twice per save.
 */
import { playerLockupPoi } from '../economy/playerLockup';
import type { GameContext } from '../core/context';
import { LOCKUP_PRICE, LOCKUP_UNLOCK_ID } from '../economy/wallet';
import type { JobBoardEntry } from '../missions/missionSystem';
import { contactPos, CONTACTS } from './contacts';
import type { BookMessage, PostInput } from './inbox';

const place = (j: JobBoardEntry): string => j.giver.name.replace(/\s*\([^)]*\)\s*$/, '');
const first = (name: string): string => name.split(' ')[0] ?? name;
const markGiver = (j: JobBoardEntry, label: string): PostInput['action'] => ({ label, mark: { x: j.giver.x, z: j.giver.z, label: `${first(j.client)} — ${place(j)}` } });

/**
 * Mags' note after each story job (spoken: her voice, docs/WRITING.md §2): what the borough made of it, and
 * that the money's counted. Keyed by mission id; anything new gets the plain line.
 */
export const SETTLED_NOTES: Readonly<Record<string, string>> = {
  'borrowed-time': 'Ronnie came in the Anchor asking who’d had his motor. I poured him a drink and said I’d ask about. Your money’s counted.',
  'wet-paper': 'Soggy or not, it got where it was going, and Wilf’s still breathing. That’ll do me. Your money’s counted.',
  'gullet-job': 'The Vanes’ “whisky” is in my cellar, and Holloway’s got an empty van and a headache. Your cut’s counted. You’re one of mine now, so act it.',
  'one-armed-bandits': 'Every landlord on the Basin heard that van go. Not one of them has rung Ronnie back. Your money’s counted.',
  'the-collection': 'Three empty doors, and a bent copper looking a fool in front of his lads. I enjoyed that more than the money. Which is counted.',
  'last-orders': 'The Chief Constable has his copy and the Post has theirs. Holloway’s cleared his desk. Your money’s counted, love. Last orders, gents.',
};
const settledNote = (id: string): string => SETTLED_NOTES[id] ?? 'Good work. Your money’s counted and nobody’s talking.';

export function wireOfflineFeed(ctx: GameContext, post: (m: PostInput) => BookMessage | null): () => void {
  const board = (): JobBoardEntry[] => ctx.services.tryGet('missions')?.board() ?? [];
  const job = (id: string): JobBoardEntry | undefined => board().find((j) => j.id === id);
  const contactMark = (id: string, label: string): PostInput['action'] | undefined => {
    const c = CONTACTS.find((x) => x.id === id);
    const p = c ? contactPos(ctx, c) : null;
    return c && p ? { label, mark: { x: p.x, z: p.z, label: `${c.short} — ${c.where}` } } : undefined;
  };
  const exportsDone = (): number => ctx.services.tryGet('save')?.data.stats.exportsDone ?? 0;

  /** After a job: who's next, Halloran's and Sid's introductions (deferred a tick so the board has moved on). */
  function afterJob(id: string): void {
    const done = job(id);
    if (!done) return;
    if (done.kind === 'export') {
      const next = board().find((j) => j.kind === 'export' && j.available);
      if (next)
        post({
          key: `export-next:${exportsDone()}`,
          from: done.client,
          kind: 'offer',
          subject: 'That one’s on the boat',
          text: `Paid, as promised. Next on my list: ${next.title.replace(/^Export:\s*/, '')}. Same as before — steal one and bring it to Halloran Motors in one piece.`,
          action: markGiver(next, 'Mark Halloran’s yard on the map'),
        });
      return;
    }
    if (done.kind !== 'story') return;
    const next = board().find((j) => j.kind === 'story' && j.available);
    post({
      key: `done:${id}`,
      from: done.client,
      kind: 'story',
      subject: `“${done.title}” — settled`,
      text: next
        ? `${settledNote(id)}\n\nWhen you’re ready there’s more: “${next.title}”. ${next.giver.phone ? `Go to ${place(next)} and answer it when it rings.` : `Come and see me at ${place(next)}.`}`
        : `${settledNote(id)}\n\nThat’s the lot from me — the borough’s yours now. Halloran always wants motors for export, Rita races on the quay, Dot needs drivers on the cabs, and Lenny pays for every matchbook you turn up. Your book has the lot.`,
      ...(next ? { action: markGiver(next, next.giver.phone ? 'Mark the phone box on the map' : `Mark ${place(next)} on the map`) } : {}),
    });
    const exp = board().find((j) => j.kind === 'export' && j.available);
    if (exp)
      post({
        key: 'export-open',
        from: exp.client,
        kind: 'offer',
        subject: 'Motors wanted',
        text: `Mags says you can drive and keep your mouth shut. I ship motors abroad and pay cash on delivery.\n\nToday I want: ${exp.title.replace(/^Export:\s*/, '')}. Steal one and bring it to Halloran Motors in one piece.`,
        action: markGiver(exp, 'Mark Halloran’s yard on the map'),
      });
    const sid = contactMark('sid', 'Mark the scrap yard on the map');
    if (sid)
      post({
        key: 'fence-intro',
        from: 'Sid Marlowe',
        kind: 'tip',
        subject: 'If you need persuading',
        text: 'Heard you’re working for Mags now. Fists only get you so far. I’ve a cosh, a blade, and for the right money something louder.\n\nGorringe’s scrap yard, Cinder Yards. Ask for Sid.',
        action: sid,
      });
  }

  let lastMoney = ctx.state.money;
  let hadPursuit = false;
  const offs = [
    ctx.events.on('missionStarted', (e) => {
      const j = job(e.id);
      if (!j || !j.giver.phone) return;
      // The call happens on screen (the phone box rings, the voice on the line); the note is the record.
      post({ key: `call:${e.id}:${ctx.services.tryGet('save')?.data.stats.missionsCompleted ?? 0}`, from: j.client, kind: 'call', subject: `On the phone — ${place(j)}`, text: j.blurb, read: true });
    }),
    ctx.events.on('missionCompleted', (e) => {
      // A replay from the book moves nothing on: no "settled" note, no next job.
      if (e.replay) return;
      setTimeout(() => afterJob(e.id), 0);
    }),
    ctx.events.on('heatChanged', (e) => {
      if (e.level >= 2) hadPursuit = true;
      // The word comes once you've shaken them off the first time (not in the middle of the chase).
      if (e.level === 0 && hadPursuit) {
        post({
          key: 'police-tip',
          from: 'Sid Marlowe',
          kind: 'tip',
          subject: 'A word about the law',
          text: 'Heard you gave the law the run-around. Next time: coppers round here can’t see round corners and they hate the alleys. Lose them, sit tight, and if the police know your car, Pike’s Respray on the Dock Road will change its colour for a price.',
          ...(contactMark('pike', 'Mark Pike’s on the map') ? { action: contactMark('pike', 'Mark Pike’s on the map') } : {}),
        });
      }
      if (e.level >= 4 && e.previous < 4) {
        post({
          key: 'raid-warning',
          from: 'Mags Delaney',
          kind: 'warning',
          subject: 'The whole borough’s out',
          text: 'Every car the nick owns is out looking for you. Get off the main roads, swap motors where nobody’s watching, and don’t come near the Anchor till it’s quiet.',
        });
      }
    }),
    ctx.events.on('playerBusted', () => {
      post({
        key: 'nicked-1',
        from: 'Mags Delaney',
        kind: 'story',
        subject: 'A night in the cells',
        text: 'Heard you had a night on the bench at the nick. Happens to the best of us. Pay the fine, keep your head down, and don’t keep me waiting.',
      });
    }),
    ctx.events.on('moneyChanged', (e) => {
      const was = lastMoney;
      lastMoney = e.amount;
      if (was < LOCKUP_PRICE && e.amount >= LOCKUP_PRICE && !ctx.services.tryGet('save')?.hasUnlock(LOCKUP_UNLOCK_ID)) {
        const lockup = playerLockupPoi(ctx.services.tryGet('layout')?.pois ?? []);
        post({
          key: 'lockup-offer',
          from: 'The landlord',
          kind: 'offer',
          subject: 'Lockup going',
          text: `${lockup?.name ?? 'A lockup'} is yours for £${LOCKUP_PRICE.toLocaleString('en-GB')} cash, with a Vesper GT inside. Wreck it and there’s another the next morning. No questions.`,
          ...(lockup ? { action: { label: 'Mark the lockup on the map', mark: { x: lockup.pos.x, z: lockup.pos.z, label: lockup.name } } } : {}),
        });
      }
    }),
  ];
  return () => {
    for (const off of offs) off();
  };
}
